/**
 * Reading the authoring graph — the payload of `GET api/data-sources/{id}/graph`.
 *
 * The graph is the CATALOGUE: which traversals exist, what each one demands of the author, and
 * which columns every reachable node can project. This module only reads it. Nothing here knows
 * the name of an entity, a table or a traversal, so the day the engine serves task boards the
 * relations step offers them without an edit (DS-11).
 *
 * Two shapes matter, both verified against `DataSourceGraphVM.cs` and the live payload recorded in
 * the plan's Wave B1 entry:
 *
 *   graph.nodes[] = {kind, table, columns[{name, dataType, nullable, label, origin}]}
 *   graph.edges[] = {fromKind, targetKind, targetTable, cardinality, discriminatorRequired,
 *                    discriminators[], strategies[]}
 *   graph.maxEdges = the server's cap on edges in one definition
 *
 * `label` and `origin` are null on physical columns. A custom field arrives named `cf:<guid>`
 * with its human wording in `label` and `origin: 'customField'` — the NAME is the wire word the
 * definition stores, the LABEL is what a person should ever read.
 *
 * Every reader tolerates a missing graph and missing arrays. The graph endpoint is keyed by
 * definition id, so a source that has never been saved has NO graph at all: the step still renders
 * the relations already built, it just cannot offer new ones.
 */

import { CompositeStrategy, isScalarStrategy } from '../../model/definitionVocabulary';
import { fanOutEdgesFormOneChain } from '../../model/definitionModelHelpers';

export { fanOutEdgesFormOneChain };

/** Cardinality wire words (`DataSourceCardinalityWords`). */
export const GraphCardinality = Object.freeze({
  ToOne: 'toOne',
  ToMany: 'toMany',
});

/** Column origins the graph reports (`DataSourceGraphColumnVM.Origin`); physical columns carry null. */
export const GraphColumnOrigin = Object.freeze({
  CustomField: 'customField',
});

/**
 * What a person reads for a graph column: its human label when the graph carries one, else its
 * name. Custom fields are named `cf:<guid>` on the wire — that name is what the definition
 * stores and the compiler reads, so it must keep flowing underneath; this is for the surfaces a
 * curator looks at.
 */
export function displayNameOfColumn(column) {
  if (!column) return '';
  return column.label || column.name || '';
}

/** Types whose values order by time. Matched by substring, so `datetime2` and `date` both count. */
const DateLikeTypeFragments = Object.freeze(['date', 'time']);

/**
 * Types the substring test catches but that are not moments in time. `timestamp` is SQL Server's
 * old name for `rowversion` — a binary counter, not a date — and the tag table really does expose
 * one, so without this it would be offered under "Date columns" on every tag relation.
 */
const NotDateTypes = Object.freeze(['timestamp', 'rowversion']);

/**
 * The column `latest` prefers to rank by when the author has not said otherwise.
 *
 * This is the framework's own audit column, stamped on every row the platform writes — not a guess
 * about any one entity's meaning. It matters because the tag table exposes 284 columns, nine of
 * them dates, and the first in schema order is a manufacture date: defaulting to "whatever date
 * came first" would quietly rank inspections by when the equipment was built. It is a PREFERENCE
 * with a fallback, never a filter, so a source that has no such column still gets a default.
 */
const PreferredOrderByColumn = 'CreateDate';

const list = (value) => (Array.isArray(value) ? value : []);

/** The node entry for a kind, or null when the graph does not describe it. */
export function graphNodeOf(graph, kind) {
  if (!graph || !kind) return null;
  return list(graph.nodes).find((node) => node && node.kind === kind) || null;
}

/** A node's projectable columns, or an empty list when the graph does not describe it. */
export function columnsOf(graph, kind) {
  const node = graphNodeOf(graph, kind);
  return node ? list(node.columns) : [];
}

/** Every traversal the graph offers out of a node kind. */
export function graphEdgesFrom(graph, fromKind) {
  if (!graph || !fromKind) return [];
  return list(graph.edges).filter((edge) => edge && edge.fromKind === fromKind);
}

/** The exact traversal an edge uses; definitions before v3 retain pair fallback. */
export function findGraphEdge(graph, fromKind, targetKind, traversalKey) {
  const candidates = graphEdgesFrom(graph, fromKind)
    .filter((edge) => edge.targetKind === targetKind);
  if (!traversalKey) return candidates[0] || null;
  return candidates.find((edge) => edge.traversalKey === traversalKey) || null;
}

export function graphEdgeIdentity(graphEdge) {
  return (graphEdge && (graphEdge.traversalKey || graphEdge.targetKind)) || '';
}

export function graphEdgeLabel(graphEdge) {
  return (graphEdge && (graphEdge.label || graphEdge.targetKind)) || '';
}

/** The tag kinds this traversal may be pinned to. Empty when the target table holds one kind. */
export function discriminatorsOf(graphEdge) {
  return graphEdge ? list(graphEdge.discriminators) : [];
}

/** True when the server will refuse this traversal unless the author names a tag kind. */
export function requiresDiscriminator(graphEdge) {
  return !!graphEdge && graphEdge.discriminatorRequired === true;
}

/** True when the traversal needs one admitted dynamic target before it can be saved. */
export function requiresTargetId(graphEdge) {
  return !!graphEdge && graphEdge.targetIdRequired === true;
}

/**
 * The strategies to offer for one traversal, in the graph's own order.
 *
 * Two filters, both of which exist to stop the wizard offering a move that would be refused
 * downstream rather than to second-guess the graph:
 *
 * - A strategy the definition model does not accept is dropped. `normalizeStrategy` THROWS on an
 *   unknown word, so a radio the model cannot take is a crash waiting for a click. The strategy
 *   vocabulary is closed and copied from the server, unlike the entity kinds, which are the
 *   graph's to decide.
 * - `fanOut` is dropped when the proposed edge would create an incomparable sibling branch.
 *   Linear descendants stay available because they refine one grain instead of multiplying two
 *   independent branches.
 */
export function strategyOptionsOf(graphEdge, state, candidate) {
  const known = Object.keys(CompositeStrategy).map((key) => CompositeStrategy[key]);

  return list(graphEdge && graphEdge.strategies)
    .filter((kind) => known.indexOf(kind) !== -1)
    .filter((kind) => kind !== CompositeStrategy.FanOut || fanOutEdgesFormOneChain(state, {
      ...candidate,
      strategy: { kind },
    }));
}

/** True when the column's SQL type orders by time. */
export function isDateLikeColumn(column) {
  const type = column && column.dataType ? String(column.dataType).toLowerCase() : '';
  if (NotDateTypes.indexOf(type) !== -1) return false;
  return DateLikeTypeFragments.some((fragment) => type.indexOf(fragment) !== -1);
}

/**
 * The columns offered as `latest`'s ordering column: date-like ones first, then the rest.
 *
 * The rest are kept rather than filtered away because the server asks only for a safe column name
 * — a definition may legitimately rank by a revision number or a sequence — and because a target
 * whose date column is typed as something unexpected would otherwise offer nothing at all.
 */
export function orderByOptionsOf(graph, targetKind) {
  const columns = columnsOf(graph, targetKind);
  const dated = columns.filter(isDateLikeColumn);
  const rest = columns.filter((column) => !isDateLikeColumn(column));
  return dated.concat(rest);
}

/**
 * The column `latest` starts on: the framework's own audit column when the target has one, else
 * the first date-like column, else the first column of any type, else null.
 */
export function defaultOrderByOf(graph, targetKind) {
  const options = orderByOptionsOf(graph, targetKind);
  if (!options.length) return null;

  const preferred = options.find((column) => (
    String(column.name).toLowerCase() === PreferredOrderByColumn.toLowerCase()
  ));

  return preferred ? preferred.name : options[0].name;
}

/** `To-many` / `To-one` as the graph reports it, or null when it does not. */
export function cardinalityOf(graphEdge) {
  if (!graphEdge) return null;
  if (graphEdge.cardinality === GraphCardinality.ToMany) return GraphCardinality.ToMany;
  if (graphEdge.cardinality === GraphCardinality.ToOne) return GraphCardinality.ToOne;
  return null;
}

/**
 * True when this definition may hold another relation. `maxEdges` is the server's cap; the
 * definition model deliberately does not enforce it (it never sees the graph), so the step that
 * adds edges is where it is honoured.
 */
export function canAddMoreEdges(graph, edgeCount) {
  const cap = graph && typeof graph.maxEdges === 'number' ? graph.maxEdges : null;
  return cap === null ? true : edgeCount < cap;
}

/**
 * True when a traversal can be added with one click: nothing to decide. Anything that needs a tag
 * kind, an ordering column, a name for the column it adds, or that would change what a row means,
 * goes through the configure panel so the author sees the decision they are making.
 */
export function isOneClickTraversal(graphEdge, state, candidate) {
  if (requiresTargetId(graphEdge)
    || requiresDiscriminator(graphEdge)
    || discriminatorsOf(graphEdge).length > 0) return false;

  const strategies = strategyOptionsOf(graphEdge, state, candidate);
  if (strategies.length !== 1) return false;

  const only = strategies[0];
  return only !== CompositeStrategy.Latest
    && only !== CompositeStrategy.FanOut
    && !isScalarStrategy(only);
}
